import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useClient } from '../context/ClientContext';
import supabase from '../lib/supabase';
import SerpScraper from '../lib/serpScraper';

const { FiPlay, FiPause, FiRefreshCw, FiActivity, FiClock, FiCheck, FiX, FiLoader } = FiIcons;

const SerpTrackingPanel = () => {
  const [queueStats, setQueueStats] = useState({
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0
  });
  const [isTracking, setIsTracking] = useState(false);
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentClient } = useClient();
  
  // Initialize the SERP scraper
  const scraper = new SerpScraper();

  useEffect(() => {
    if (currentClient) {
      fetchQueueStats();
      fetchRecentResults();
      
      // Set up interval to refresh stats
      const interval = setInterval(fetchRecentResults, 30000);
      return () => clearInterval(interval);
    }
  }, [currentClient]);

  const fetchQueueStats = async () => {
    try {
      // Get tracking job stats from Supabase
      const { data, error } = await supabase
        .from('serp_tracking_jobs_seo_87a5cd9f')
        .select('status')
        .eq('client_id', currentClient?.id || '')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      // Count jobs by status
      const stats = {
        waiting: 0,
        active: 0,
        completed: 0,
        failed: 0
      };
      
      data.forEach(job => {
        if (job.status === 'pending') stats.waiting++;
        if (job.status === 'processing') stats.active++;
        if (job.status === 'completed') stats.completed++;
        if (job.status === 'failed') stats.failed++;
      });
      
      setQueueStats(stats);
      setIsTracking(stats.active > 0 || stats.waiting > 0);
    } catch (error) {
      console.error('Error fetching queue stats:', error);
    }
  };

  const fetchRecentResults = async () => {
    if (!currentClient) return;
    
    try {
      const { data, error } = await supabase
        .from('rankings_seo_87a5cd9f')
        .select(`
          *,
          keyword_location_tracking_seo_87a5cd9f (
            keywords_seo_87a5cd9f ( keyword ),
            locations_seo_87a5cd9f ( name, city, state )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      setRecentResults(data || []);
    } catch (error) {
      console.error('Error fetching recent results:', error);
    }
  };

  const startManualTracking = async () => {
    if (!currentClient) return;
    setLoading(true);
    
    try {
      // Get a few active tracking pairs for manual testing
      const { data: trackingPairs, error } = await supabase
        .from('keyword_location_tracking_seo_87a5cd9f')
        .select(`
          id,
          keyword_id,
          location_id,
          keywords_seo_87a5cd9f ( id, keyword, client_id ),
          locations_seo_87a5cd9f ( id, name, city, state, latitude, longitude, address )
        `)
        .eq('is_active', true)
        .eq('keywords_seo_87a5cd9f.client_id', currentClient.id)
        .limit(3);
      
      if (error) throw error;
      
      if (!trackingPairs || trackingPairs.length === 0) {
        alert('No active keyword tracking found. Please add some keywords first.');
        return;
      }
      
      // Create tracking jobs in the database
      for (const pair of trackingPairs) {
        const keyword = pair.keywords_seo_87a5cd9f;
        const location = pair.locations_seo_87a5cd9f;
        
        if (keyword && location) {
          // Insert job record
          await supabase
            .from('serp_tracking_jobs_seo_87a5cd9f')
            .insert([{
              keyword_location_id: pair.id,
              client_id: currentClient.id,
              status: 'processing'
            }]);
          
          // Process the job immediately
          try {
            const result = await scraper.searchGoogle(
              keyword.keyword,
              location
            );
            
            if (result.success) {
              await scraper.storeResults(keyword.id, location.id, result);
              
              // Update job status
              await supabase
                .from('serp_tracking_jobs_seo_87a5cd9f')
                .update({ status: 'completed', completed_at: new Date().toISOString() })
                .eq('keyword_location_id', pair.id)
                .eq('status', 'processing');
            } else {
              throw new Error(result.error);
            }
          } catch (jobError) {
            console.error(`Error processing job for ${keyword.keyword}:`, jobError);
            
            // Update job status to failed
            await supabase
              .from('serp_tracking_jobs_seo_87a5cd9f')
              .update({ 
                status: 'failed', 
                completed_at: new Date().toISOString(),
                error_message: jobError.message
              })
              .eq('keyword_location_id', pair.id)
              .eq('status', 'processing');
          }
        }
      }
      
      alert(`Processed ${trackingPairs.length} SERP tracking jobs!`);
      await fetchQueueStats();
      await fetchRecentResults();
    } catch (error) {
      console.error('Error starting manual tracking:', error);
      alert('Error starting tracking: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-blue-500';
      case 'completed': return 'text-green-500';
      case 'failed': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return FiLoader;
      case 'completed': return FiCheck;
      case 'failed': return FiX;
      default: return FiClock;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">SERP Tracking</h2>
          <p className="text-gray-600">Real-time search engine results monitoring</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={startManualTracking}
            disabled={loading || !currentClient}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50"
          >
            <SafeIcon icon={loading ? FiLoader : FiPlay} className={loading ? 'animate-spin' : ''} />
            <span>{loading ? 'Starting...' : 'Start Tracking'}</span>
          </button>
          <button onClick={fetchQueueStats} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <SafeIcon icon={FiRefreshCw} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Queue Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Waiting</p>
              <p className="text-2xl font-bold text-gray-800">{queueStats.waiting}</p>
            </div>
            <SafeIcon icon={FiClock} className="text-gray-400 text-xl" />
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">Active</p>
              <p className="text-2xl font-bold text-blue-800">{queueStats.active}</p>
            </div>
            <SafeIcon icon={FiActivity} className="text-blue-500 text-xl" />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Completed</p>
              <p className="text-2xl font-bold text-green-800">{queueStats.completed}</p>
            </div>
            <SafeIcon icon={FiCheck} className="text-green-500 text-xl" />
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Failed</p>
              <p className="text-2xl font-bold text-red-800">{queueStats.failed}</p>
            </div>
            <SafeIcon icon={FiX} className="text-red-500 text-xl" />
          </div>
        </div>
      </div>

      {/* Recent Results */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Tracking Results</h3>
        {recentResults.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiActivity} className="text-4xl mb-2 mx-auto" />
            <p>No tracking results yet</p>
            <p className="text-sm">Start tracking to see results here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentResults.slice(0, 5).map((result, index) => {
              const keyword = result.keyword_location_tracking_seo_87a5cd9f?.keywords_seo_87a5cd9f;
              const location = result.keyword_location_tracking_seo_87a5cd9f?.locations_seo_87a5cd9f;
              return (
                <motion.div
                  key={result.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">
                      {keyword?.keyword || 'Unknown Keyword'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {location ? `${location.city}, ${location.state}` : 'Unknown Location'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-800">
                      #{result.rank === 999 ? 'Not Found' : result.rank}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(result.date_checked).toLocaleDateString()}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Indicator */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            <span className="text-sm text-gray-600">
              {isTracking ? 'Tracking active' : 'Tracking idle'}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SerpTrackingPanel;